import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import * as styles from './OverflowMenu.scss';

/**
 * @class OverflowMenu
 * @classdesc Reusable OverflowMenu Component
 * @property {Object} this.state
 * @property {Object} menuRef
 * @example ./OverflowMenu.md
 */
export default class OverflowMenu extends Component {
  static propTypes = {
    className: PropTypes.string,
    listClassName: PropTypes.string,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        func: PropTypes.func.isRequired,
        text: PropTypes.string.isRequired,
        style: PropTypes.objectOf(PropTypes.string),
        dangerBtn: PropTypes.bool,
        disabled: PropTypes.bool,
      }),
    ),
  };

  static defaultProps = {
    className: null,
    listClassName: null,
    options: [],
  };

  constructor(props) {
    super(props);
    this.state = {
      open: false,
    };
    this.menuRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  /**
   * Handles click event on overflow menu item
   * @param {Function} clickCallback - Callback from Overflow MenuItem
   * @return {Function} clickCallBack
   */
  handleClick = clickCallback => {
    this.handleClose();
    return clickCallback();
  };

  /**
   * Verifies if click locations are outside of component or not
   * @param {Object} - Event object
   * @return {Function|null} - return handleClose or nothing based on click location
   */
  handleClickOutside = e => {
    if (this.menuRef && !this.menuRef.current.contains(e.target)) return this.handleClose();
    return null;
  };

  /**
   * Resets menu open state to false
   * @return {Function} setState() - Updates `open` value
   */
  handleClose = () => {
    return this.setState({ open: false });
  };

  buildMenuOptions = () => {
    const { options } = this.props;

    return options.map(option => {
      return (
        <li
          key={option.text}
          className={classNames(
            styles.menuItem,
            { [styles.dangerBtn]: option.dangerBtn },
            { [styles.disabledBtn]: option.disabled }
          )}
          onClick={option.disabled
            ? null
            : () => this.handleClick(option.func)}
          style={option.style}>
          <span>{option.text}</span>
        </li>
      );
    });
  };

  toggleMenu = () => {
    this.setState(prevState => ({ open: !prevState.open }));
  };

  render() {
    const { className, listClassName } = this.props;
    const { open } = this.state;

    return (
      <div className={classNames(styles.overflowMenu, className)} ref={this.menuRef}>
        <div className={styles.menuBtn} onClick={this.toggleMenu}>
          <span>&#183;</span>
          <span>&#183;</span>
          <span>&#183;</span>
        </div>
        {open &&
          <div className={styles.contentContainer}>
            <ul className={classNames(styles.menuList, listClassName)}>
              {this.buildMenuOptions()}
            </ul>
          </div>}
      </div>
    );
  }
}
